import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Download, Trash2, FileText, Check, AlertCircle } from 'lucide-react';
import { useAuth, isViewingAs, VIEWING_AS_NOTE } from '../context/AuthContext';
import { CONSENT_SCOPES } from './ConsentFields';

/**
 * Privacy & data section for MyAccountPage (GDPR Week 6).
 *
 *  - 4-layer consent toggles backed by GET/PUT /api/me/consents.
 *    The server age-gates; we also hide scopes the cohort can't have
 *    (allowedScopes from the API) so under-16s never even see "audience".
 *  - DOB prompt if the account predates age-gating (PATCH /api/me/profile).
 *  - DSAR buttons — copy of my data / delete my data — each logs a
 *    data_subject_requests row that Marakin staff fulfil within 30 days.
 */

const DSAR_LABELS = {
  access: 'Copy of your data',
  portability: 'Machine-readable export',
  deletion: 'Account & data deletion',
  rectification: 'Correction request',
};

export default function PrivacySettings() {
  const qc = useQueryClient();
  const { user, impersonating } = useAuth();
  // B-65 review: consents and GDPR requests are the person's own (the server
  // refuses them on a staff "view as" session).
  const viewingAs = isViewingAs(impersonating);
  const [dob, setDob] = useState('');
  const [notice, setNotice] = useState(null); // { kind: 'ok'|'err', text }

  // Per-user query keys: cached privacy data must never leak across an
  // account switch on a shared device (see AuthContext qc.clear()).
  const { data, isLoading } = useQuery({
    queryKey: ['me-consents', user?.id],
    queryFn: () => axios.get('/me/consents').then(r => r.data),
  });
  const { data: requests = [] } = useQuery({
    queryKey: ['me-data-requests', user?.id],
    queryFn: () => axios.get('/me/data-requests').then(r => r.data),
  });

  const saveConsent = useMutation({
    mutationFn: (change) => axios.put('/me/consents', change).then(r => r.data),
    onSuccess: (fresh) => qc.setQueryData(['me-consents', user?.id], (old) => ({ ...old, ...fresh })),
    onError: (err) => setNotice({ kind: 'err', text: err.response?.data?.error || 'Could not save — try again.' }),
  });

  const saveDob = useMutation({
    mutationFn: () => axios.patch('/me/profile', { dateOfBirth: dob }).then(r => r.data),
    onSuccess: () => { setNotice({ kind: 'ok', text: 'Date of birth saved.' }); qc.invalidateQueries({ queryKey: ['me-consents'] }); },
    onError: (err) => setNotice({ kind: 'err', text: err.response?.data?.error || 'Could not save date of birth.' }),
  });

  const raiseDsar = useMutation({
    mutationFn: (requestType) => axios.post('/me/data-requests', { requestType }).then(r => r.data),
    onSuccess: (row) => {
      setNotice({ kind: 'ok', text: `${DSAR_LABELS[row.request_type]} requested — we'll respond within 30 days.` });
      qc.invalidateQueries({ queryKey: ['me-data-requests'] });
    },
    onError: (err) => setNotice({ kind: 'err', text: err.response?.data?.error || 'Could not submit the request.' }),
  });

  if (isLoading) return <div className="account-card"><p className="muted" style={{ fontSize: 14 }}>Loading privacy settings…</p></div>;

  const consents = data?.consents || {};
  const allowed = data?.allowedScopes || [];
  const openTypes = new Set(requests.filter(r => ['received', 'in_progress'].includes(r.status)).map(r => r.request_type));

  return (
    <>
      {!data?.hasDateOfBirth && (
        <div className="account-card" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 14, marginBottom: 8 }}>
            <strong>Add your date of birth.</strong> We use it to apply the right privacy
            protections for your age — some choices below stay locked until it's set.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="input" type="date" style={{ maxWidth: 200 }} value={dob}
              max={new Date().toISOString().slice(0, 10)} onChange={e => setDob(e.target.value)} />
            <button className="btn btn-primary btn-sm" disabled={!dob || saveDob.isPending} onClick={() => saveDob.mutate()}>
              {saveDob.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="account-card">
        <p className="muted" style={{ fontSize: 14, marginBottom: 10 }}>
          We always store the basics needed to run your club account — that can't be switched
          off. Everything else is up to you. Full details in the{' '}
          <Link to="/privacy" style={{ color: 'var(--green)', textDecoration: 'underline' }}>privacy policy</Link>.
        </p>

        {CONSENT_SCOPES.map(s => {
          const permitted = allowed.includes(s.key);
          return (
            <label key={s.key} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 8px',
              borderTop: '1px solid var(--border)', cursor: permitted ? 'pointer' : 'default',
              opacity: permitted ? 1 : 0.5,
            }}>
              <input
                type="checkbox"
                checked={!!consents[s.key]}
                disabled={viewingAs || !permitted || saveConsent.isPending}
                onChange={e => saveConsent.mutate({ [s.key]: e.target.checked })}
                style={{ marginTop: 3, accentColor: 'var(--green, #1E4A35)' }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                <strong style={{ fontWeight: 700 }}>{s.label}</strong>
                <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13 }}>
                  {s.detail}
                  {!permitted && ' Not available for your age group.'}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {viewingAs && <p className="muted" style={{ fontSize: 13, margin: '8px 0 0' }}>{VIEWING_AS_NOTE}</p>}

      <div className="account-card" style={{ marginTop: 12 }}>
        <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Your data rights</p>
        <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          You can ask for a copy of everything we hold about you, or ask us to delete it.
          We respond within 30 days.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" disabled={viewingAs || openTypes.has('access') || raiseDsar.isPending}
            onClick={() => raiseDsar.mutate('access')}>
            <Download size={14} /> {openTypes.has('access') ? 'Copy requested' : 'Request a copy of my data'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={viewingAs || openTypes.has('deletion') || raiseDsar.isPending}
            onClick={() => {
              if (window.confirm('Request deletion of your account and personal data?\n\nA Marakin admin will contact you to confirm before anything is removed. Club records you appear in (e.g. match results) may be retained where the club has a legitimate need.')) {
                raiseDsar.mutate('deletion');
              }
            }}>
            <Trash2 size={14} /> {openTypes.has('deletion') ? 'Deletion requested' : 'Request deletion'}
          </button>
          <Link to="/privacy" className="btn btn-ghost btn-sm"><FileText size={14} /> Privacy policy</Link>
        </div>

        {requests.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {requests.slice(0, 4).map(r => (
              <div key={r.id} style={{ padding: '4px 0' }}>
                {DSAR_LABELS[r.request_type] || r.request_type} — {r.status.replace('_', ' ')}
                {' · '}{new Date(r.received_at).toLocaleDateString('en-GB')}
              </div>
            ))}
          </div>
        )}
      </div>

      {notice && (
        <p style={{
          marginTop: 10, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
          color: notice.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
        }}>
          {notice.kind === 'ok' ? <Check size={14} /> : <AlertCircle size={14} />} {notice.text}
        </p>
      )}
    </>
  );
}
